import { Sparkles } from "lucide-react";
import { usePricing, formatINR } from "../lib/pricing";

/** Renders the current price with a strike-through regular price + a "₹250 off · N left"
 * pill when the launch offer is still active. When slots are exhausted, only the
 * regular price is shown. Layout adapts to the parent's text color via `tone`. */
export function PriceLine({ tone = "dark", align = "left", size = "lg", showBadge = true, testId }) {
    const p = usePricing();
    const isDark = tone === "dark"; // "dark" = dark text on light bg; "light" = light text on dark bg
    const textPrimary = isDark ? "text-charcoal" : "text-ivory";
    const textStrike = isDark ? "text-charcoal/40" : "text-ivory/50";
    const badgeBg = isDark ? "bg-gold/15 text-gold" : "bg-gold/25 text-gold";
    const currentSize = size === "xl"
        ? "text-5xl md:text-6xl"
        : size === "lg"
            ? "text-4xl md:text-5xl"
            : size === "md"
                ? "text-2xl md:text-3xl"
                : "text-xl";
    const strikeSize = size === "xl" || size === "lg" ? "text-lg" : "text-sm";
    const justify = align === "center" ? "justify-center" : "justify-start";

    return (
        <div className={`flex flex-col ${align === "center" ? "items-center" : "items-start"} gap-2`} data-testid={testId}>
            <div className={`flex items-baseline gap-3 ${justify}`}>
                <span className={`font-serif leading-none ${currentSize} ${textPrimary}`} data-testid="price-current">
                    {formatINR(p.current_paise)}
                </span>
                {p.is_launch && (
                    <span className={`font-serif line-through ${strikeSize} ${textStrike}`} data-testid="price-regular">
                        {formatINR(p.regular_paise)}
                    </span>
                )}
            </div>
            {showBadge && p.is_launch && (
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10.5px] font-medium uppercase tracking-[0.18em] ${badgeBg}`}
                    data-testid="price-launch-badge"
                >
                    <Sparkles className="h-3 w-3" />
                    ₹250 off · {p.slots_left} of {p.slots_total} left
                </span>
            )}
        </div>
    );
}

/** Small pill "₹250 off · N of 1000 left" for use next to prices. */
export function LaunchPill({ tone = "dark", testId }) {
    const p = usePricing();
    if (!p.is_launch) return null;
    const cls = tone === "dark"
        ? "bg-gold/15 text-gold ring-1 ring-gold/20"
        : "bg-gold/25 text-gold ring-1 ring-gold/30";
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9.5px] font-semibold uppercase tracking-[0.18em] ${cls}`}
            data-testid={testId || "launch-pill"}
        >
            <Sparkles className="h-2.5 w-2.5" />
            ₹250 off · {p.slots_left}/{p.slots_total} left
        </span>
    );
}

export function PriceInline({ tone = "dark", testId }) {
    const p = usePricing();
    const strikeColor = tone === "dark" ? "text-charcoal/40" : "text-ivory/50";
    return (
        <span className="inline-flex items-baseline gap-1.5" data-testid={testId}>
            <span className="font-medium">{formatINR(p.current_paise)}</span>
            {p.is_launch && (
                <span className={`text-[0.7em] line-through ${strikeColor}`}>{formatINR(p.regular_paise)}</span>
            )}
        </span>
    );
}
